import apiClient from '../service/apiClient';
import { create } from 'zustand';
import { isAxiosError } from 'axios';
import { Page, ResponseError } from '../type/type';
import { toast } from 'react-toastify';
import { formatPhoneNumber } from '../util/util';

// 계정 타입
export interface ICustomer {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  loginId: string;
  name: string;
  telNo: string;
  franchiseId: string;
}

// 본인 계정 응답 타입
type CustomerResponse = ICustomer;

// 본인 계정 업데이트 요청 타입
export type UpdateCustomerRequest = {
  name: string;
  telNo: string;
};

type UpdatePasswordRequest = {
  loginPw: string;
};

type CheckPasswordRequest = {
  loginId: string;
  loginPw: string;
};

// Zustand Store Data 타입
type CustomerData = {
  // 로딩
  getCustomerLoading: boolean;
  updateCustomerLoading: boolean;
  updateCustomerPasswordLoading: boolean;

  // 본인 계정
  myCustomer: ICustomer | null;
};

// Zustand Store Action 타입
type CustomerAction = {
  // 본인 계정 조회
  getMyCustomer: () => void;

  // 본인 계정 업데이트
  updateMyCustomer: (customerData: UpdateCustomerRequest) => void;

  // 비밀번호 업데이트
  updateCustomerPassword: (
    updatePasswordData: UpdatePasswordRequest,
    checkPasswordData: CheckPasswordRequest
  ) => void;
};

export const useCustomerStore = create<CustomerData & CustomerAction>(
  (set, get) => ({
    // 로딩
    getCustomerLoading: false,
    updateCustomerLoading: false,
    updateCustomerPasswordLoading: false,

    // 본인 계정
    myCustomer: null,

    // 본인 계정 조회
    getMyCustomer: async () => {
      set({ getCustomerLoading: true });

      try {
        const response = await apiClient.get<CustomerResponse>(
          '/customer/who-am-i'
        );

        const formattedCustomer = {
          ...response.data,
          telNo: formatPhoneNumber(response.data.telNo),
        };

        set({ myCustomer: formattedCustomer });
      } catch (error) {
        console.error('API 요청 실패:', error);
        toast.error('계정 정보를 가져오는데 실패했습니다.');
      }
    },

    // 본인 계정 수정
    updateMyCustomer: async customerData => {
      set({ updateCustomerLoading: true });
      try {
        const response = await apiClient.patch(`/customer`, customerData);

        if (response.status === 200) {
          toast.success('계정 정보를 수정했습니다.');
          get().getMyCustomer();
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('API 요청 실패:', error);
          toast.error('계정 정보를 수정하는데 실패했습니다.');
        }
      }
      set({ updateCustomerLoading: false });
    },

    // 비밀번호 업데이트
    updateCustomerPassword: async (
      UpdatePasswordRequest,
      CheckPasswordRequest
    ) => {
      set({ updateCustomerPasswordLoading: true });
      try {
        const checkResponse = await apiClient.post(
          `/login`,
          CheckPasswordRequest
        );

        if (checkResponse.status !== 200) {
          toast.error('비밀번호가 일치하지 않습니다.');
          return;
        }

        const response = await apiClient.post(
          `/customer/pw-change`,
          UpdatePasswordRequest
        );

        if (response.status === 204) {
          toast.success('비밀번호를 수정했습니다.');
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('API 요청 실패:', error);
          toast.error('비밀번호를 수정하는데 실패했습니다.');
        }
      }
      set({ updateCustomerPasswordLoading: false });
    },
  })
);
